import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { subscribeShiftChanges } from '../lib/realtime';

export interface ComplianceVerdict {
  verdict: 'allowed' | 'blocked' | 'establishing' | 'unknown';
  is_compliant: boolean;
  evidence_available?: boolean;
  compliance_established_at?: string | null;
  monitoring_since?: string | null;
  compliance_expires_at: string | null;
  minutes_until_expiry: number | null;
  drive_left_min: number;
  worked_min_rolling: number;
  match_type: 'continuous' | 'split' | null;
  beslutsunderlag: {
    window_start: string;
    evaluated_at: string;
    server_time_verified: boolean;
    stint_events: number;
    trips_considered: number;
    sessions_considered: number;
    qualifying_rest: {
      started_at: string; ended_at: string; minutes: number;
      match_type: string | null; source: string;
    } | null;
    excluded_work_from_trips: boolean;
    compliance_established_at?: string | null;
    note?: string | null;
  };
}

/**
 * THE single legal verdict — same server-side engine (Edge Function
 * compliance-verdict) the mobile app and government view use. The dashboard no
 * longer computes dygnsvila compliance itself; it consumes the one authority.
 */
export function useComplianceVerdict(driverId: string | undefined) {
  const [verdict, setVerdict] = useState<ComplianceVerdict | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!driverId) return;
    const { data, error: err } = await supabase.functions.invoke('compliance-verdict', {
      body: { driver_id: driverId },
    });
    if (err) { setError(err.message); setLoading(false); return; }
    setVerdict(data as ComplianceVerdict);
    setError(null);
    setLoading(false);
  }, [driverId]);

  useEffect(() => {
    if (!driverId) { setLoading(false); return; }
    refetch();
    const timer = setInterval(refetch, 30000);
    const unsub = subscribeShiftChanges(driverId, refetch);
    return () => { clearInterval(timer); unsub(); };
  }, [driverId, refetch]);

  return { verdict, loading, error };
}
